import React from 'react';
import {
  ImageField,
  InputField,
  SelectField,
  VideoField,
  CalendarField,
  TimeField,
  SwitchField,
  TitleField,
  VisualSelectField,
  NumberField,
  RichTextField
} from './field-types';
import { SingleFieldProps } from '../../types/form-renderer';
import { getClientHandler } from '../../handlers';
import { useClientId } from '../../hooks/use-client-id';
import { FORBIDDEN_CHARS } from '../../constants/clients/shared/forbidden-chars';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';

interface Props extends SingleFieldProps {
  setFieldError?: (key: string, error?: string) => void;
}

export const FormFieldRenderer: React.FC<Props> = ({
  field,
  formData,
  onFieldChange,
  fieldWidth,
  errors = {},
  creativityType,
  setFieldError
}) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const clientId = useClientId();
  const clientHandler = getClientHandler(clientId);

  if (clientHandler.shouldShowField) {
    const shouldShow = clientHandler.shouldShowField(field.key, formData, creativityType);
    if (!shouldShow) return null;
  }

  const getDynamicOptions = () => {
    if (clientHandler.getDynamicOptions) {
      const dynamicOpts = clientHandler.getDynamicOptions(
        field.key,
        { ...formData, _creativityType: creativityType },
        field.options
      );
      if (dynamicOpts) return dynamicOpts;
    }
    return field.options;
  };

  const commonStyle = {
    width: '100%',
    maxWidth: fieldWidth,
    marginBottom: spacing.md,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'flex-start' as const,
    fontSize: screens.xs ? fontSize.lg : fontSize.base
  };

  const labelStyle = {
    fontSize: screens.xs ? fontSize.lg : fontSize.base,
    letterSpacing: '0.5px',
    marginBottom: 8
  };

  const fieldProps = {
    field: { ...field, options: getDynamicOptions() },
    value: formData[field.key],
    onChange: (value: any) => onFieldChange(field.key, value),
    commonStyle,
    labelStyle,
    error: errors[field.key]
  };

  const validateForbiddenChars = (value: string, forbidden: string[]): string | null => {
    if (!forbidden || forbidden.length === 0) return null;
    for (const char of forbidden) {
      if ((value || '').includes(char)) {
        return `El nombre contiene caracteres no permitidos: "${char}"`;
      }
    }
    return null;
  };

  switch (field.type) {
    case 'input': {
      const handleBlur = (value: any) => {
        if (field.key === 'title' && setFieldError) {
          const validationError = validateForbiddenChars(value, FORBIDDEN_CHARS);
          setFieldError(field.key, validationError || undefined);
        }
      };

      if (field.key === 'title') {
        return (
          <TitleField
            key={field.key}
            {...fieldProps}
            formData={formData}
            onBlur={() => handleBlur(formData[field.key])}
          />
        );
      }

      return <InputField key={field.key} {...fieldProps} onBlur={() => handleBlur(formData[field.key])} />;
    }

    case 'select':
      if (field.key === 'day' && formData.date) return null;
      return <SelectField key={field.key} {...fieldProps} />;

    case 'video':
      return <VideoField key={field.key} {...fieldProps} />;

    case 'image':
      return <ImageField key={field.key} {...fieldProps} />;

    case 'calendar':
      return (
        <CalendarField
          key={field.key}
          {...fieldProps}
          field={field}
          value={formData[field.key]}
          onChange={(value: any) => onFieldChange(field.key, value)}
          commonStyle={commonStyle}
          labelStyle={labelStyle}
          error={errors[field.key]}
          formData={formData}
          onFieldChange={onFieldChange}
        />
      );

    case 'time':
      return <TimeField key={field.key} {...fieldProps} />;

    case 'switch':
      return <SwitchField key={field.key} {...fieldProps} />;

    case 'visual-select':
      return <VisualSelectField key={field.key} {...fieldProps} />;

    case 'number':
      return <NumberField key={field.key} {...fieldProps} />;

    case 'rich-text': {
      let maxLines = field.maxLines || 1;

      if (clientHandler.getMaxLinesForVariant) {
        const { stage, adChannel, variant } = formData;
        if (stage && adChannel && variant) {
          maxLines = clientHandler.getMaxLinesForVariant(
            { ...formData, _creativityType: creativityType },
            stage,
            adChannel,
            variant
          );
        }
      }

      return <RichTextField key={field.key} {...fieldProps} field={{ ...fieldProps.field, maxLines }} />;
    }

    default:
      return null;
  }
};
